import React, { useState, useCallback, useRef, useEffect } from "react";
import { Copy, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@dashai/components/ui/tooltip";
import { FeedbackPopover } from "./FeedbackPopover";
import { useMessageFeedbackContext } from "@dashai/hooks/useMessageFeedback";
import { MessageFeedback } from "@dashai/types/message-feedback";
import { cn } from "@dashai/lib/utils";

interface MessageHoverToolbarProps {
  messageId: string;
  chatId: string;
  role: "user" | "assistant";
  createdAt: string;
  messageContentRef: React.RefObject<HTMLDivElement | null>;
  isVisible: boolean;
  onToolbarMouseEnter: () => void;
  onToolbarMouseLeave: () => void;
  /**
   * Whether the message exists in the chat store. Votes and copy events are
   * recorded against the stored message, so an unsaved one offers copy only.
   */
  stored?: boolean;
  /**
   * Extra classes for the thumbs feedback boxes. They open in a portal at the
   * page root, so a host that sits above z-50 passes a higher layer here.
   */
  feedbackClassName?: string;
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatFullDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function MessageHoverToolbar({
  messageId,
  chatId,
  role,
  createdAt,
  messageContentRef,
  isVisible,
  onToolbarMouseEnter,
  onToolbarMouseLeave,
  stored = true,
  feedbackClassName,
}: MessageHoverToolbarProps) {
  const [copied, setCopied] = useState(false);
  const [popoverType, setPopoverType] = useState<"positive" | "negative" | null>(null);
  const copyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    feedbackMap,
    submitFeedback,
    removeFeedback,
    updateFeedbackDetails,
    triggerLearningExtraction,
    logCopyEvent,
  } = useMessageFeedbackContext();

  useEffect(() => {
    return () => {
      if (copyTimeout.current) clearTimeout(copyTimeout.current);
    };
  }, []);

  const feedback: MessageFeedback | undefined = feedbackMap[messageId];
  const canRate = role === "assistant" && stored;

  const handleCopy = useCallback(async () => {
    if (!messageContentRef.current) return;

    const el = messageContentRef.current;
    const html = el.innerHTML;
    const text = el.innerText;

    try {
      const htmlBlob = new Blob([html], { type: "text/html" });
      const textBlob = new Blob([text], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob,
        }),
      ]);
    } catch {
      // Fallback for browsers that don't support ClipboardItem
      await navigator.clipboard.writeText(text);
    }

    setCopied(true);
    if (copyTimeout.current) clearTimeout(copyTimeout.current);
    copyTimeout.current = setTimeout(() => setCopied(false), 2000);

    // Log copy event (fire-and-forget)
    if (stored) logCopyEvent(messageId, chatId);
  }, [messageContentRef, messageId, chatId, logCopyEvent, stored]);

  const handleThumbsUp = useCallback(() => {
    if (feedback?.type === "positive") {
      removeFeedback(messageId);
      setPopoverType(null);
    } else {
      submitFeedback(messageId, chatId, "positive");
      setPopoverType("positive");
    }
  }, [feedback, messageId, chatId, submitFeedback, removeFeedback]);

  const handleThumbsDown = useCallback(() => {
    if (feedback?.type === "negative") {
      removeFeedback(messageId);
      setPopoverType(null);
    } else {
      submitFeedback(messageId, chatId, "negative");
      setPopoverType("negative");
    }
  }, [feedback, messageId, chatId, submitFeedback, removeFeedback]);

  const handleFeedbackSubmit = useCallback(
    async (categories: string[], comment?: string) => {
      if (feedback) {
        await updateFeedbackDetails(messageId, chatId, categories, comment);
        if (feedback.type === "negative" && comment?.trim()) {
          triggerLearningExtraction(messageId, chatId, categories, comment);
        }
      }
      setPopoverType(null);
    },
    [feedback, messageId, chatId, updateFeedbackDetails, triggerLearningExtraction]
  );

  return (
    <div
      data-hover-toolbar
      className={cn(
        "absolute -bottom-7 left-0 z-10 flex items-center gap-1 transition-opacity duration-150",
        isVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      )}
      onMouseEnter={onToolbarMouseEnter}
      onMouseLeave={onToolbarMouseLeave}
    >
      {/* Copy button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleCopy}
            className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            aria-label="Copy message"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-[11px] px-2 py-1">
          {copied ? "Copied!" : "Copy"}
        </TooltipContent>
      </Tooltip>

      {/* Thumbs Up (stored AI messages only) */}
      {canRate && (
        <Tooltip>
          <FeedbackPopover
            type="positive"
            open={popoverType === "positive"}
            onOpenChange={(open) => {
              if (!open) setPopoverType(null);
            }}
            onSubmit={handleFeedbackSubmit}
            className={feedbackClassName}
          >
            <TooltipTrigger asChild>
              <button
                onClick={handleThumbsUp}
                className={cn(
                  "h-6 w-6 flex items-center justify-center rounded transition-colors",
                  feedback?.type === "positive"
                    ? "text-primary"
                    : "text-muted-foreground/50 hover:text-muted-foreground"
                )}
                aria-label="Mark as helpful"
                aria-pressed={feedback?.type === "positive"}
              >
                <ThumbsUp
                  className={cn("h-3.5 w-3.5", feedback?.type === "positive" && "fill-current")}
                />
              </button>
            </TooltipTrigger>
          </FeedbackPopover>
          <TooltipContent side="bottom" className="text-[11px] px-2 py-1">
            {feedback?.type === "positive" ? "Remove rating" : "Helpful"}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Thumbs Down (stored AI messages only) */}
      {canRate && (
        <Tooltip>
          <FeedbackPopover
            type="negative"
            open={popoverType === "negative"}
            onOpenChange={(open) => {
              if (!open) setPopoverType(null);
            }}
            onSubmit={handleFeedbackSubmit}
            className={feedbackClassName}
          >
            <TooltipTrigger asChild>
              <button
                onClick={handleThumbsDown}
                className={cn(
                  "h-6 w-6 flex items-center justify-center rounded transition-colors",
                  feedback?.type === "negative"
                    ? "text-destructive"
                    : "text-muted-foreground/50 hover:text-muted-foreground"
                )}
                aria-label="Mark as unhelpful"
                aria-pressed={feedback?.type === "negative"}
              >
                <ThumbsDown
                  className={cn("h-3.5 w-3.5", feedback?.type === "negative" && "fill-current")}
                />
              </button>
            </TooltipTrigger>
          </FeedbackPopover>
          <TooltipContent side="bottom" className="text-[11px] px-2 py-1">
            {feedback?.type === "negative" ? "Remove rating" : "Not helpful"}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Timestamp */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="px-1 text-[11px] text-muted-foreground/40 select-none cursor-default tabular-nums">
            {formatTime(createdAt)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-[11px] px-2 py-1">
          {formatFullDate(createdAt)}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
